import CarouselPageArrow from 'app/components/partials/carousel-page-arrow';
import CategoryFeature from 'app/components/partials/category-feature';
import Icon from 'app/components/partials/icon';
import { Link } from 'react-router-dom';
import Loader from 'app/components/partials/loader';
import { MODE } from 'app/ducks/filtered-news';
import { NEWS } from 'config/news';
import PropTypes from 'prop-types';
import React from 'react';
import Slider from 'react-slick';
import StoryCard from 'app/components/partials/story-card';
import {
    buildCategoryParam,
    buildListingSearch,
    getListingLimit,
    getListingPages,
    getPageWindow,
    getTotalPages,
    getVisibleCount,
    hasFeatureRow,
    LAYOUT,
    PAGE_GAP,
    resolveCategories,
    toggleCategory
} from 'app/utilities/news-listing';

const SLIDES_TO_SHOW = 3;

// Room left above the listing when it is scrolled back into view, so its heading
// clears the header fixed over the top of the page
const HEADER_CLEARANCE = 100;

// How close to the end of the run the reader has to get, counted in screens of
// articles, before the page after it is fetched. Enough of a margin to cover the
// request, and not so much that it is fetched for readers who never get there.
const PREFETCH_SCREENS = 2;

// What the carousel should do once articles that were being fetched arrive
const SCROLL = {
    BACK: 'back',
    FORWARD: 'forward',
    HOLD: 'hold'
};

const SLIDER_CONFIG = {
    arrows: true,
    autoplay: false,
    dots: false,
    draggable: true,
    // Reaching an end is what hands over to the next page, and looping would
    // mean the carousel never reaches one. It also keeps slick from cloning
    // slides, which would put every article in the HTML twice.
    infinite: false,
    slidesToScroll: SLIDES_TO_SHOW,
    slidesToShow: SLIDES_TO_SHOW,
    speed: 500,
    swipeToSlide: true,
    // Breakpoints match --from-normal-screen-w and --from-small-screen-w so the
    // slide count and the card styling change at the same widths
    responsive: [
        { breakpoint: 1000, settings: { slidesToShow: 2, slidesToScroll: 2 } },
        { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } }
    ]
};

/*
    The value the listing endpoint filters by: the ids of the ticked categories,
    falling back to the category of the page itself when nothing is ticked. The
    `/news` landing page has neither, which the endpoint reads as every category.

    Ids rather than the slugs from the URL because a slug is only unique within
    its parent, and the endpoint takes either.
*/
const getCategoryParam = ({ activeCategories, categories, pageCategoryId }) => {
    const ids = resolveCategories(categories, activeCategories).map((category) => category.id);

    return buildCategoryParam(ids) || pageCategoryId || null;
};

class FilterStories extends React.Component {
    constructor(props) {
        super(props);

        // Counts the listings that have been shown, so that a new one gets a
        // carousel of its own rather than inheriting the position of the one it
        // replaced. slick clamps an out of range position itself, at the end of
        // an animation and so after anything set here, which is a listing opening
        // at its last article. Starting the carousel over sidesteps the argument.
        this.state = { listing: 0 };

        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.handleSlideStart = this.handleSlideStart.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.slider = null;
        this.carousel = null;
        this.section = null;
        // What to do with the carousel once the articles being fetched arrive,
        // and where it was sitting when they were asked for
        this.pendingScroll = null;
        this.slideBeforeLoad = 0;
        // Set whenever the run of pages is started afresh, and consumed by the
        // one backfill that follows. Landing deep in the listing should pull in
        // the couple of pages before it, not walk all the way back to the first.
        this.canBackfill = true;
    }

    componentDidMount() {
        // Server side rendering already put the right listing in the store, so a
        // fetch here would only duplicate it. Anything the store holds for
        // another listing does need replacing though, because it is shared state.
        if (this.isStoreCurrent()) {
            this.backfill();

            return;
        }

        this.loadCurrent();
    }

    componentDidUpdate(prevProps) {
        const { excludeFeatured, isStale, items, lastPage, listingPage } = this.props;
        const hasRequestChanged = getCategoryParam(prevProps) !== getCategoryParam(this.props) ||
            Boolean(prevProps.excludeFeatured) !== Boolean(excludeFeatured) ||
            prevProps.listingPage !== listingPage;

        if (hasRequestChanged) {
            // A page of its own replaces what was on screen rather than adding
            // to it, so the reader is put at the top of it the way following a
            // link between pages would
            if (this.isPaged() && prevProps.listingPage !== listingPage) {
                this.scrollToListing();
            }

            this.loadCurrent();

            return;
        }

        if (this.isCards()) {
            // The URL follows the articles rather than leading them, so that
            // adding a page cannot be mistaken for a request for one
            if (this.isCumulative() && lastPage !== prevProps.lastPage) {
                this.syncPageParam(lastPage);
            }

            return;
        }

        // A replacement listing has landed in place of the one that was on
        // screen, so the carousel starts over: it may be sitting at a slide that
        // no longer exists, and it is a different listing to read regardless
        if (prevProps.isStale && !isStale) {
            this.setState(({ listing }) => ({ listing: listing + 1 }));
            this.backfill();

            return;
        }

        const added = Array.isArray(items) && Array.isArray(prevProps.items) ?
            items.length - prevProps.items.length :
            0;

        if (added > 0) {
            this.applyPendingScroll(added, prevProps.items.length);

            return;
        }

        this.backfill();
    }

    /**
     * @return {boolean} whether the articles are laid out as rows of cards
     *                   rather than as a carousel
     */
    isCards() {
        return this.props.layout !== LAYOUT.CAROUSEL;
    }

    /**
     * @return {boolean} whether a page of this listing is every page up to it,
     *                   which is what "More stories" adds to
     */
    isCumulative() {
        return this.props.layout === LAYOUT.GRID;
    }

    /**
     * @return {boolean} whether a page of this listing is the slice it names,
     *                   reached by numbered links rather than by adding to
     */
    isPaged() {
        return this.props.layout === LAYOUT.PAGED;
    }

    /**
     * Whether the listing opens with the feature row rather than going straight
     * into the grid.
     *
     * Only an exact count of featured stories is laid out this way. The row is
     * two stacked stories across two thirds and three cards down the other
     * third, so a different count would leave the two columns different heights,
     * and the articles it takes out of the listing would stop dividing the rest
     * of the grid into whole rows.
     *
     * @return {boolean} true when the feature row is in use
     */
    hasFeature() {
        return this.isCumulative() && hasFeatureRow(this.props.featuredNews);
    }

    /**
     * Whether the store already holds the page the URL asks for, as part of a run
     * for the same filter.
     *
     * A grid page is cumulative, so holding more than was asked for still counts:
     * the run starts at the first page either way and the extra articles are the
     * next pages, not somebody else's.
     *
     * @return {boolean} true when nothing needs fetching
     */
    isStoreCurrent() {
        const { currentCategory, currentExcludeFeatured, excludeFeatured, firstPage, items, lastPage, listingPage } = this.props;

        if (!Array.isArray(items) || currentCategory !== getCategoryParam(this.props)) {
            return false;
        }

        // One category answers with two different listings depending on whether
        // the stories it features are among them, so articles held for the other
        // one are somebody else's however well the category matches
        if (currentExcludeFeatured !== Boolean(excludeFeatured)) {
            return false;
        }

        if (this.isCumulative()) {
            return firstPage === 1 && lastPage >= listingPage;
        }

        return listingPage >= firstPage && listingPage <= lastPage;
    }

    /*
        Fetches whatever the URL asks for that the store does not already hold,
        joining it to the run when it sits directly against either end so the
        carousel is added to rather than rebuilt.
    */
    loadCurrent() {
        const { firstPage, lastPage, listingPage } = this.props;

        if (this.isStoreCurrent()) {
            return;
        }

        // Every page up to the one asked for, in one request each, because the
        // grid shows them all at once rather than a page at a time
        if (this.isCumulative()) {
            this.loadPages(getListingPages(LAYOUT.GRID, listingPage), MODE.REPLACE, null);

            return;
        }

        // One page in its place, which is the whole of what this shape shows
        if (this.isPaged()) {
            this.loadPages([listingPage], MODE.REPLACE, null);

            return;
        }

        if (listingPage === lastPage + 1) {
            this.loadPages([listingPage], MODE.APPEND, SCROLL.FORWARD);

            return;
        }

        if (listingPage === firstPage - 1) {
            this.loadPages([listingPage], MODE.PREPEND, SCROLL.BACK);

            return;
        }

        this.loadPages([listingPage], MODE.REPLACE, null);
    }

    /*
        Adds the next page to the end of the grid. The link this comes from points
        at that page, so the reader without JavaScript gets the same articles as a
        page of their own rather than nothing at all.
    */
    handleLoadMore(event) {
        const { isBusy, isStale, lastPage } = this.props;

        if (isBusy || isStale) {
            event.preventDefault();

            return;
        }

        event.preventDefault();
        this.loadPages([lastPage + 1], MODE.APPEND, null);
    }

    /**
     * Moves the URL onto the last page the grid holds, so that reloading or
     * sharing it comes back to the same articles.
     *
     * @param {number} page
     */
    syncPageParam(page) {
        const { activeCategories = [], listingPage, navigate } = this.props;

        if (!navigate || page === listingPage) {
            return;
        }

        navigate({ search: buildListingSearch(activeCategories, page) }, { replace: true });
    }

    /*
        Pulls in the pages just before the run, so someone arriving partway
        through the listing is not left with a single page of articles and has the
        earlier ones already in the carousel behind them.
    */
    backfill() {
        const { firstPage, isBusy, isStale, items } = this.props;

        // Nothing is filled in around articles that are on their way out, and a
        // request for them would cancel the one replacing them. Only the
        // carousel fills in at all: it reads as one continuous run, where the
        // shapes that lay out rows of cards show one page at a time.
        if (this.isCards() || !this.canBackfill || isBusy || isStale ||
            !Array.isArray(items) || !items.length || firstPage <= 1) {
            return;
        }

        const from = Math.max(1, firstPage - NEWS.BACKFILL_PAGES);
        const pages = [];

        for (let page = from; page < firstPage; page++) {
            pages.push(page);
        }

        // Consumed whether or not it reaches the first page, so arriving deep in
        // the listing costs one extra request rather than a chain of them
        this.canBackfill = false;
        this.loadPages(pages, MODE.PREPEND, SCROLL.HOLD);
    }

    /*
        Puts the top of the listing back in view, for a page that replaces what
        was on screen rather than adding to it. Without this, following a page
        link from the foot of one page lands on the foot of the next.
    */
    scrollToListing() {
        if (!this.section || typeof window === 'undefined') {
            return;
        }

        const top = this.section.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({ top: Math.max(0, top - HEADER_CLEARANCE), behavior: 'smooth' });
    }

    loadPages(pages, mode, scroll) {
        this.pendingScroll = scroll;
        // Noted now rather than when the articles land, because by then the
        // carousel has been re-rendered around them and the position it is being
        // moved on from is gone
        this.slideBeforeLoad = this.getCurrentSlide();

        if (mode === MODE.REPLACE) {
            this.canBackfill = true;
        }

        this.props.loadData(
            getCategoryParam(this.props),
            pages,
            mode,
            getListingLimit(this.props.layout),
            Boolean(this.props.excludeFeatured)
        );
    }

    /*
        Puts the carousel where the articles that just arrived should leave it:
        `added` is how many joined the list and `previousLength` how long it was
        before they did.
    */
    applyPendingScroll(added, previousLength) {
        const scroll = this.pendingScroll;

        this.pendingScroll = null;

        // Articles added at the end: move on to the first of them, otherwise the
        // arrow that loaded them looks like it did nothing
        if (scroll === SCROLL.FORWARD) {
            this.goToSlide(previousLength);

            return;
        }

        if (scroll !== SCROLL.HOLD && scroll !== SCROLL.BACK) {
            return;
        }

        // Articles added at the front push everything along, so holding the
        // reader in place means moving on by however many arrived
        const held = this.slideBeforeLoad + added;

        if (scroll === SCROLL.HOLD) {
            this.goToSlide(held);

            return;
        }

        // Stepping back was asked for, so land on the screen before the one being
        // held rather than on it, or the arrow would look like it did nothing.
        // One move rather than holding and then stepping, because slick ignores a
        // step made while it is still animating the hold.
        this.goToSlide(Math.max(0, held - this.getSlidesInView()));
    }

    /*
        The slides slick has marked as being in view, read off the rendered
        carousel rather than worked out from the breakpoints here. Their count
        doubles as the step size, because SLIDER_CONFIG scrolls a full screen at
        every breakpoint.
    */
    getActiveSlides() {
        return this.carousel ? [...this.carousel.querySelectorAll('.slick-slide')] : [];
    }

    /**
     * @return {number} how many slides the carousel is showing at this width
     */
    getSlidesInView() {
        const inView = this.getActiveSlides().filter((slide) => slide.classList.contains('slick-active')).length;

        return inView || SLIDES_TO_SHOW;
    }

    /**
     * @return {number} the index of the leftmost slide in view
     */
    getCurrentSlide() {
        const index = this.getActiveSlides().findIndex((slide) => slide.classList.contains('slick-active'));

        return index < 0 ? 0 : index;
    }

    goToSlide(index) {
        if (this.slider) {
            this.slider.slickGoTo(index);
        }
    }

    /*
        Fetched as the carousel starts moving rather than once it has arrived, so
        the request has the length of the animation as a head start.
    */
    handleSlideStart(current, next) {
        this.prefetchAhead(next);
    }

    /**
     * Fetches the page after the run while the reader is still short of its end,
     * so the arrow they reach it with already has articles to show instead of
     * standing still waiting on a response.
     *
     * Only once they are close, because pulling the next page in on load would
     * double what every visitor downloads to save a wait most never reach.
     *
     * @param {number} index the leftmost slide the carousel is moving to
     */
    prefetchAhead(index) {
        const { currentCategory, isBusy, items, lastPage, resultCount } = this.props;

        if (isBusy || !Array.isArray(items) || currentCategory !== getCategoryParam(this.props)) {
            return;
        }

        if (index + (this.getSlidesInView() * PREFETCH_SCREENS) < items.length) {
            return;
        }

        const page = lastPage + 1;

        if (page > getTotalPages(resultCount, NEWS.FILTER_LIMIT)) {
            return;
        }

        // No scroll intent: the reader is the one moving the carousel, so it has
        // to stay where they have put it and the articles simply appear beyond
        // the last one, which is the whole point of fetching them early
        this.loadPages([page], MODE.APPEND, null);
    }

    /*
        Moves the URL onto the page whose articles are now on screen.

        Without this the address only changes when an arrow runs out of slides
        and fetches, so scrolling within the run, or back through it, would leave
        the URL, the title and the canonical tag on whichever page was loaded
        first. Sharing or reloading would then not land where the reader was.

        Replaced rather than pushed: scrolling a carousel is not something the
        back button should have to be walked through a screen at a time.
    */
    handleSlideChange(index) {
        const { activeCategories = [], firstPage, isStale, listingPage, navigate } = this.props;
        // The run is contiguous and every page is a full slice, so which page a
        // slide belongs to is just how far into the run it sits
        const page = firstPage + Math.floor(index / NEWS.FILTER_LIMIT);

        // Articles being replaced are no longer the run the page numbers
        // describe, so where they sit says nothing about which page is on screen
        if (isStale || !navigate || page === listingPage) {
            return;
        }

        navigate({ search: buildListingSearch(activeCategories, page) }, { replace: true });
    }

    /**
     * Where a carousel arrow goes once the carousel has run out of slides, or
     * null when there is no page that way.
     *
     * Just outside the run already loaded rather than either side of the page in
     * the URL, because that is the page the arrow would need to fetch. The two
     * are the same until a backfill widens the run, and it is the difference
     * between being able to keep stepping back and being stuck at the arrow.
     *
     * @param {number} offset -1 for the previous page, 1 for the next
     *
     * @return {object|null} a react-router location
     */
    getPageLink(offset) {
        const { activeCategories = [], firstPage, lastPage, resultCount } = this.props;
        const page = offset < 0 ? firstPage - 1 : lastPage + 1;

        if (page < 1 || page > getTotalPages(resultCount, NEWS.FILTER_LIMIT)) {
            return null;
        }

        return { search: buildListingSearch(activeCategories, page) };
    }

    /**
     * Ticking a category filters in place rather than navigating, so these are
     * links that only change the query. Links rather than checkbox inputs so
     * they work without JavaScript and can be followed by crawlers.
     *
     * Several can be ticked at once and each link carries the whole resulting
     * selection, which is what lets a no-JavaScript client combine filters too.
     *
     * @return {object|null} sidebar
     */
    renderFilters() {
        const { activeCategories = [], categories } = this.props;

        if (!categories || !categories.length) {
            return null;
        }

        return (
            <div className={this.isCards() ? 'filter-stories-cell' : 'filter-stories-sidebar'}>
                <h2 className="filter-stories-title">Filter stories</h2>
                <hr className="filter-stories-divider" />
                <ul className="filter-stories-options">
                    {categories.map(({ id, name, slug }) => {
                        const isChecked = activeCategories.includes(slug);

                        return (
                            <li key={id}>
                                {/*
                                    Ticking is multi select, so every combination of
                                    categories is a URL reachable by following these,
                                    and the count of them doubles with each option.
                                    The canonical keeps them out of the index, but
                                    only nofollow keeps a crawler from walking the
                                    whole set. Nothing is reached solely through a
                                    filter: each category has a page of its own, and
                                    the articles are linked from the listing itself.
                                */}
                                <Link
                                    to={{ search: buildListingSearch(toggleCategory(categories, activeCategories, slug), 1) }}
                                    className="checkbox-wrapper"
                                    rel="nofollow"
                                    role="checkbox"
                                    aria-checked={isChecked ? 'true' : 'false'}>
                                    <span className={`checkbox-button${isChecked ? ' is-checked' : ''}`}>
                                        <Icon className="icon" name="check" />
                                    </span>
                                    {name}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    /**
     * The message shown in place of articles, when there are none to show.
     *
     * @return {object|null} message, or null when there are articles
     */
    renderMessage() {
        const { activeCategories = [], items } = this.props;

        // Articles are only ever taken down once their replacements are ready, so
        // holding none at all means a first load rather than a change of filter
        if (!Array.isArray(items)) {
            return <div className="filter-stories-message"><Loader /></div>;
        }

        if (items.length) {
            return null;
        }

        if (!activeCategories.length) {
            return <p className="filter-stories-message">No stories found.</p>;
        }

        return <p className="filter-stories-message">No stories found for {activeCategories.length > 1 ? 'these categories' : 'this category'}.</p>;
    }

    renderCarousel() {
        const { items } = this.props;

        // Articles waiting to be replaced are faded and taken out of reach, so
        // the section holds its place and its shape while the new ones are
        // fetched without the reader being able to act on what is leaving
        const className = `filter-stories-carousel${this.props.isStale ? ' is-stale' : ''}`;

        return (
            <div className={className} ref={(el) => { this.carousel = el; }}>
                <Slider
                    key={this.state.listing}
                    ref={(slider) => { this.slider = slider; }}
                    {...SLIDER_CONFIG}
                    beforeChange={this.handleSlideStart}
                    afterChange={this.handleSlideChange}
                    prevArrow={<CarouselPageArrow direction="previous" label="Previous stories" to={this.getPageLink(-1)} />}
                    nextArrow={<CarouselPageArrow direction="next" label="More stories" to={this.getPageLink(1)} />}
                >
                    {/* Keyed by article so appending a page reuses the slides
                        already on screen instead of rebuilding them */}
                    {items.map((item) => <StoryCard key={item.id} item={item} />)}
                </Slider>
            </div>
        );
    }

    /*
        The filter block and the cards share one grid, the filter sitting in it as
        though it were a card. That is why a level 1 category opens with eight
        articles and adds nine at a time: the block takes the first cell, and only
        whole rows are shown.
    */
    renderGrid() {
        const { activeCategories = [], isBusy, isStale, items, lastPage, resultCount } = this.props;
        const filters = this.renderFilters();
        // Kept even with nothing to list, or a filter that matches no articles
        // would take away the means of unticking it
        const message = this.renderMessage();
        const held = Array.isArray(items) ? items.length : 0;
        // The feature row has already shown the first few articles, so the grid
        // carries on from where it left off rather than repeating them. Both the
        // count held and the count in all are measured from there, which is what
        // keeps the grid's own rows whole and its last page complete.
        const lead = this.hasFeature() ? Math.min(held, NEWS.FEATURE_LEAD) : 0;
        const total = resultCount ? Math.max(0, resultCount - lead) : resultCount;
        const visible = getVisibleCount(held - lead, total, filters ? 1 : 0);
        const hasMore = !message && (!resultCount || held < resultCount);

        return (
            <div className={`filter-stories-list${isStale ? ' is-stale' : ''}`}>
                <div className="filter-stories-grid">
                    {filters}
                    {message ?
                        <div className={`filter-stories-empty${filters ? '' : ' is-full'}`}>{message}</div> :
                        items.slice(lead, lead + visible).map((item) => <StoryCard key={item.id} item={item} />)
                    }
                </div>
                {hasMore &&
                    <div className="filter-stories-actions">
                        {/* A link to the next page rather than a button, so that it
                            is followable without JavaScript and by a crawler. The
                            click is taken over to add to the grid instead. */}
                        <Link
                            to={{ search: buildListingSearch(activeCategories, lastPage + 1) }}
                            className="filter-stories-more || button primary"
                            onClick={this.handleLoadMore}
                            aria-busy={isBusy ? 'true' : 'false'}>
                            {isBusy ? 'Loading…' : 'More stories'}
                        </Link>
                    </div>
                }
            </div>
        );
    }

    /*
        One page of the listing, in place of the one before it rather than below
        it. Every article on the page is shown: it is a slice of a constant size,
        so its rows are whole apart from the last page's, and holding any of them
        back would be holding back articles no other page carries.
    */
    renderPaged() {
        const { isStale, items, layout, listingPage, resultCount } = this.props;
        const filters = this.renderFilters();
        const message = this.renderMessage();

        return (
            <div className={`filter-stories-list${isStale ? ' is-stale' : ''}`}>
                <div className="filter-stories-grid">
                    {filters}
                    {message ?
                        <div className={`filter-stories-empty${filters ? '' : ' is-full'}`}>{message}</div> :
                        items.map((item) => <StoryCard key={item.id} item={item} />)
                    }
                </div>
                {!message && this.renderPagination(listingPage, getTotalPages(resultCount, getListingLimit(layout)))}
            </div>
        );
    }

    /**
     * The numbered links a paged listing is walked with.
     *
     * Links rather than buttons, so every page is a URL a crawler can follow and
     * a reader can share, and so the listing works with no JavaScript at all.
     * With it, the click is a route change that swaps one page of cards for the
     * next without the page around them being rebuilt.
     *
     * @param {number} current Page being shown
     * @param {number} total Pages in the listing
     *
     * @return {object|null} pagination, or null when it all fits on one page
     */
    renderPagination(current, total) {
        const { activeCategories = [], isBusy } = this.props;

        if (!total || total < 2) {
            return null;
        }

        const to = (page) => ({ search: buildListingSearch(activeCategories, page) });

        return (
            <nav className="filter-stories-pagination" aria-label="Stories pages" aria-busy={isBusy ? 'true' : 'false'}>
                {/* The arrows the carousels are turned with. Kept in place at the
                    ends of the listing rather than taken away, so the row does
                    not shift sideways as the reader walks through it. */}
                <CarouselPageArrow
                    direction="previous"
                    label="Previous page"
                    modifier="filter-stories-arrow"
                    to={current > 1 ? to(current - 1) : null} />
                <ol className="filter-stories-pages">
                    {getPageWindow(current, total).map((page, index) => (
                        <li key={page === PAGE_GAP ? `${PAGE_GAP}-${index}` : page}>
                            {page === PAGE_GAP ?
                                <span className="filter-stories-page is-gap">&hellip;</span> :
                                <Link
                                    to={to(page)}
                                    className={`filter-stories-page${page === current ? ' is-current' : ''}`}
                                    aria-label={`Page ${page}`}
                                    aria-current={page === current ? 'page' : null}>
                                    {page}
                                </Link>
                            }
                        </li>
                    ))}
                </ol>
                <CarouselPageArrow
                    direction="next"
                    label="Next page"
                    modifier="filter-stories-arrow"
                    to={current < total ? to(current + 1) : null} />
            </nav>
        );
    }

    renderStories() {
        // Both card layouts render their own message, in the cells beside the
        // filter block rather than in place of the whole listing
        if (this.isPaged()) {
            return this.renderPaged();
        }

        if (this.isCumulative()) {
            return this.renderGrid();
        }

        return this.renderMessage() || this.renderCarousel();
    }

    render() {
        // A card layout holds the filter block in its own first cell, so unlike
        // the carousel it is not laid out beside the articles
        const { featuredNews, items, title } = this.props;
        const isCards = this.isCards();

        return (
            <section
                className={`filter-stories${isCards ? ' is-grid' : ''}`}
                ref={(el) => { this.section = el; }}>
                <div className="constrain-width large">
                    {/* A listing that stands on a page of its own is titled by
                        the page; one placed on a page as a content block is
                        named by the block */}
                    {title && <h2 className="filter-stories-heading">{title}</h2>}
                    {/* Inside the container the section already opens, so the row
                        lines up with the grid below it rather than being inset
                        again by a second one */}
                    {this.hasFeature() &&
                        <CategoryFeature
                            featuredNews={featuredNews}
                            stories={Array.isArray(items) ? items.slice(0, NEWS.FEATURE_LEAD) : []} />
                    }
                    <div className="filter-stories-inner">
                        {!isCards && this.renderFilters()}
                        <div className="filter-stories-stories">
                            {this.renderStories()}
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}

FilterStories.defaultProps = {
    layout: LAYOUT.CAROUSEL
};

FilterStories.propTypes = {
    // Which of the two shapes the listing takes, and so how its pages work
    layout: PropTypes.oneOf(Object.values(LAYOUT)),
    // Listing state, from the URL
    activeCategories: PropTypes.array,
    listingPage: PropTypes.number,
    // The category of the page itself, used when nothing is ticked
    pageCategoryId: PropTypes.string,
    // Sidebar options
    categories: PropTypes.array,
    // Heading above the listing, when it is not the page's own subject
    title: PropTypes.string,
    // Stories the CMS features on the category itself. An exact count of them
    // opens the listing with the feature row instead of the plain grid.
    featuredNews: PropTypes.array,
    // Whether to ask for the listing without the stories the feature row is
    // already showing, so the page does not run the same headlines twice
    excludeFeatured: PropTypes.bool,
    // Store
    currentCategory: PropTypes.string,
    currentExcludeFeatured: PropTypes.bool,
    firstPage: PropTypes.number,
    isBusy: PropTypes.bool,
    isStale: PropTypes.bool,
    items: PropTypes.array,
    lastPage: PropTypes.number,
    resultCount: PropTypes.number,
    // Functions
    loadData: PropTypes.func.isRequired,
    navigate: PropTypes.func
};

export default FilterStories;
